import { Suspense, useEffect, type ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import { OrgChoicePage } from '@nebula/shared/components/OrgChoicePage'
import { useAuthStore } from '@nebula/shared/store'
import { lazyWithRetry } from '@nebula/shared/utils'

import LoginPage from './pages/LoginPage'

// Public routes that don't require authentication
const PUBLIC_ROUTES = ['/forgot-password', '/reset-password', '/verify-email', '/invite/']

const MainLayout = lazyWithRetry(() => import('./layouts/MainLayout'))
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'))
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'))
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'))
const OrganizationsPage = lazyWithRetry(() => import('./pages/OrganizationsPage'))
const OrganizationDetailPage = lazyWithRetry(() => import('./pages/OrganizationDetailPage'))
const UsersPage = lazyWithRetry(() => import('./pages/UsersPage'))
const RolesPage = lazyWithRetry(() => import('./pages/RolesPage'))
const PermissionsPage = lazyWithRetry(() => import('./pages/PermissionsPage'))
const InfrastructurePage = lazyWithRetry(() => import('./pages/InfrastructurePage'))
const SystemLogsPage = lazyWithRetry(() => import('./pages/SystemLogsPage'))
const RedisMonitorPage = lazyWithRetry(() => import('./pages/RedisMonitorPage'))
const PlatformSettingsPage = lazyWithRetry(() => import('./pages/PlatformSettingsPage'))
const PagesDeploymentsPage = lazyWithRetry(() => import('./pages/PagesDeploymentsPage'))
const PortalDomainsPage = lazyWithRetry(() => import('./pages/PortalDomainsPage'))
const ClusterMonitoringPage = lazyWithRetry(() => import('./pages/ClusterMonitoringPage'))
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'))
const HubSpacesPage = lazyWithRetry(() => import('./pages/hub/HubSpacesPage'))
const HubStatusesPage = lazyWithRetry(() => import('./pages/hub/HubStatusesPage'))
const HubIncidentsPage = lazyWithRetry(() => import('./pages/hub/HubIncidentsPage'))
const HubRoadmapPage = lazyWithRetry(() => import('./pages/hub/HubRoadmapPage'))
const HubIssuesPage = lazyWithRetry(() => import('./pages/hub/HubIssuesPage'))
const HubGitHubIntegrationPage = lazyWithRetry(() => import('./pages/hub/HubGitHubIntegrationPage'))
const HubSettingsPage = lazyWithRetry(() => import('./pages/hub/HubSettingsPage'))
const HubAccessPage = lazyWithRetry(() => import('./pages/hub/HubAccessPage'))
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'))
const InviteAcceptPage = lazyWithRetry(() =>
  import('@nebula/shared/components').then((module) => ({ default: module.InviteAcceptPage })),
)

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-3 size-7 animate-spin rounded-full border-b-2 border-red-600" />
        <p className="text-sm text-gray-600">Carregando página...</p>
      </div>
    </div>
  )
}

function withRouteFallback(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
}

function App() {
  const { user, token, checkAuth, isAuthenticated, isLoading, orgChoiceRequired } = useAuthStore()
  const location = useLocation()
  const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route))
  const isLoginPage = location.pathname === '/login' || location.pathname === '/'

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Redirect após login se veio de convite
  useEffect(() => {
    if (user) {
      const pendingRedirect = sessionStorage.getItem('invite_redirect')
      if (pendingRedirect) {
        sessionStorage.removeItem('invite_redirect')
        window.location.replace(pendingRedirect)
      }
    }
  }, [user])

  // Show loading while checking auth (but NOT on login page to avoid remounting).
  // Só com token: sem ele o isLoading é o do login() em andamento, e trocar a LoginPage
  // pelo spinner a remontaria numa rota interna, perdendo o modal do código de 2FA (#1171)
  if (isLoading && !isLoginPage && token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-red-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  // If not authenticated or no user
  if (!isAuthenticated || !user) {
    // Allow public routes without authentication
    if (isPublicRoute) {
      return (
        <Routes>
          <Route path="/forgot-password" element={withRouteFallback(<ForgotPasswordPage />)} />
          <Route path="/reset-password" element={withRouteFallback(<ResetPasswordPage />)} />
          <Route path="/verify-email" element={withRouteFallback(<VerifyEmailPage />)} />
          <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )
    }

    return <LoginPage />
  }

  // Mais de uma org compatível e a aba não escolheu: pergunta antes de qualquer tela (#1145)
  if (orgChoiceRequired) {
    return <OrgChoicePage />
  }

  // Verificar se é super admin (RBAC — usa flag computada pelo backend via system:admin)
  const isSuperAdmin = user?.is_superuser === true

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-red-600">Acesso Negado</h1>
          <p className="mb-4 text-gray-600">Esta aplicação é restrita a Super Administradores</p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
          >
            Fazer Logout
          </button>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Invite accept page - outside MainLayout */}
      <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={withRouteFallback(<MainLayout />)}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={withRouteFallback(<DashboardPage />)} />
        <Route path="organizations" element={withRouteFallback(<OrganizationsPage />)} />
        <Route path="organizations/:id" element={withRouteFallback(<OrganizationDetailPage />)} />
        <Route path="users" element={withRouteFallback(<UsersPage />)} />
        <Route path="roles" element={withRouteFallback(<RolesPage />)} />
        <Route path="permissions" element={withRouteFallback(<PermissionsPage />)} />
        <Route path="infrastructure" element={withRouteFallback(<InfrastructurePage />)} />
        <Route path="cluster-monitoring" element={withRouteFallback(<ClusterMonitoringPage />)} />
        <Route path="system-logs" element={withRouteFallback(<SystemLogsPage />)} />
        <Route path="redis-monitor" element={withRouteFallback(<RedisMonitorPage />)} />
        <Route path="hub/spaces" element={withRouteFallback(<HubSpacesPage />)} />
        <Route path="hub/statuses" element={withRouteFallback(<HubStatusesPage />)} />
        <Route path="hub/incidents" element={withRouteFallback(<HubIncidentsPage />)} />
        <Route path="hub/roadmap" element={withRouteFallback(<HubRoadmapPage />)} />
        <Route path="hub/issues" element={withRouteFallback(<HubIssuesPage />)} />
        <Route path="hub/github" element={withRouteFallback(<HubGitHubIntegrationPage />)} />
        <Route path="hub/settings" element={withRouteFallback(<HubSettingsPage />)} />
        <Route path="hub/access" element={withRouteFallback(<HubAccessPage />)} />
        <Route path="settings" element={withRouteFallback(<PlatformSettingsPage />)} />
        <Route path="pages/deployments" element={withRouteFallback(<PagesDeploymentsPage />)} />
        <Route path="pages/domains" element={withRouteFallback(<PortalDomainsPage />)} />
        <Route
          path="settings/agents"
          element={withRouteFallback(<PlatformSettingsPage forcedTab="agents" hideTabs />)}
        />
        <Route
          path="settings/gateway-agents"
          element={withRouteFallback(<PlatformSettingsPage forcedTab="gateway-agents" hideTabs />)}
        />
        <Route
          path="settings/templates"
          element={withRouteFallback(<PlatformSettingsPage forcedTab="templates" hideTabs />)}
        />
        <Route path="profile" element={withRouteFallback(<ProfilePage />)} />
        <Route path="notifications" element={withRouteFallback(<NotificationsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
